import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  ButtonGroup,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormErrorIcon,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { CreateTeamDrawerProps } from 'features/user-groups/components/CreateTeamDrawer/types';
import { useDrawerStateDispatch } from 'features/main/context';
import { AddMembersDropdown } from 'features/user-groups/components/AddMembersDropdown/AddMembersDropdown';
import { useAddMembersToGroups } from 'features/user-group/user-group-tabs/hooks/useAddMembersToGroup';
import { USER_GROUPS_PERMISSIONS_INFO_MSG } from 'features/user-groups/components/CreateTeamDrawer/utils';

export const AddMembersDrawer: React.FC<CreateTeamDrawerProps> = () => {
  const [{ drawerVisible, drawerId }] = useDrawerStateDispatch();
  const {
    values,
    errors,
    handleSubmit,
    isSubmitting,
    handleMembersSelect,
    handleCloseDrawer,
  } = useAddMembersToGroups();

  const isOpen = drawerVisible && drawerId === 'add-members-drawer';

  // Always show error if no members are selected
  const showMembersError = !!errors.members;

  return (
    <Drawer isOpen={isOpen} onClose={handleCloseDrawer} size='lg'>
      <DrawerOverlay />
      <form onSubmit={handleSubmit} data-testid='add-member-form' noValidate>
        <DrawerContent data-testid='add-member-drawer'>
          <DrawerHeader display='flex' alignItems='center'>
            Add users to group
            <DrawerCloseButton />
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={6} align='stretch'>
              <Alert status='info' data-testid='user-groups-permissions-info-alert'>
                <AlertIcon />
                <AlertDescription>{USER_GROUPS_PERMISSIONS_INFO_MSG}</AlertDescription>
              </Alert>
              <FormControl
                data-testid='create-team-members-field'
                isInvalid={showMembersError}
                isRequired
              >
                <FormLabel htmlFor='team-members'>Users</FormLabel>
                <AddMembersDropdown
                  onMembersSelect={handleMembersSelect}
                  value={values.members}
                />
                {showMembersError && (
                  <FormErrorMessage>
                    <FormErrorIcon />
                    {errors.members}
                  </FormErrorMessage>
                )}
              </FormControl>
            </VStack>
          </DrawerBody>

          <DrawerFooter justifyContent='flex-end'>
            <ButtonGroup>
              <Button
                variant='ghost'
                onClick={handleCloseDrawer}
                isDisabled={isSubmitting}
                data-testid='group-add-member-cancel-button'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='solid'
                isLoading={isSubmitting}
                isDisabled={isSubmitting || showMembersError}
                data-testid='group-add-member-submit-button'
              >
                Save
              </Button>
            </ButtonGroup>
          </DrawerFooter>
        </DrawerContent>
      </form>
    </Drawer>
  );
};
